import { ReportingDefaultReportSets } from '../../../../i18n'
import {
    agentsURL,
    CACHE_TIME,
    callRecordingIntegrationsURL,
    defaultReportsListURL,
    devicesURL,
    EXTENDED_CACHE_TIME,
    getAllTelSysAccessibleByUser,
    getClientConfigurationListURL,
    getCMEURL,
    getCRMConfigurationListURL,
    getSimulationModesURL,
    groupMembershipURL,
    LOCALES_URL,
    partitionsURL,
    partitionURL,
    telServerURL,
    TelSysFields,
    telSysURL
} from '../../../NonReact/constants/ApiConstants'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { PartitionValues } from '../../AddUser/Types'
import { SelectOptions } from '../Components/Selects'
import {
    AgentSelectItem,
    DeviceSelectItem,
    EntitySelectOption,
    SelectItem,
    SelectOption
} from '../Components/Selects/Types'
import {
    Agent,
    CallRecordingIntegration,
    CME,
    DefaultReportItem,
    Device,
    GenericAgentsCollections,
    GenericDeviceCollections,
    IndividualTelItem,
    LocaleEntries,
    Partition,
    TelSys
} from '../Types/Types'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import { PARTITION_LICENCES } from '../../../NonReact/constants/TelSysConstants'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { ClientConfigurationsResponse } from '../../../NonReact/Types/ClientConfigurationTypes'
import { ANY_DEVICE_VALUE } from '../Constants/DefaultValues'
import { AdminUserResponse, APIData, APIListData, JSONResponse } from '../../../NonReact/common/Types/Types'
import { CRMConfigurationsResponse } from '../../../NonReact/Types/CRMConfiguration/Types'

export const ALL_VALUE: number = -1
export const INVALID_VALUE: number = -3
export const EMPTY_VALUE: string = ''

export const ALL_TEL_OPTION: SelectOption<number> = {
    label: '[All Telephony Servers]',
    value: ALL_VALUE
}

// Get the list of CMEs.
export const getCMEsAsSelectOptions = (
    fields?: string[],
    filterFunction?: (cme: CME) => boolean
): Promise<SelectOptions<string>> =>
    requestGetJSON<APIListData<CME>>(getCMEURL(fields), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIListData<CME>>): SelectOptions<string> => {
            const availableCMEs: SelectOptions<string> =
                res?.Data?.Items?.filter(filterFunction ?? ((): boolean => true))?.map(
                    ({ Description, CMEnvironmentID }: CME): SelectOption<string> => ({
                        label: Description,
                        value: CMEnvironmentID
                    })
                ) ?? []

            return availableCMEs
        }
    )

// Get the list of Tel Servers.
export const getTelServersAsSelectOptionsWithCMEScopeCheck = (
    cmeID: string,
    recursive?: boolean,
    userInfo?: AdminUserResponse,
    telSysFields?: string[],
    ...filterByLicenceLevel: ReportingAccessLevels[]
): Promise<EntitySelectOption<number, TelSys>[]> =>
    requestGetJSON<APIListData<TelSys>>(telServerURL(cmeID, recursive, telSysFields, ...filterByLicenceLevel), {
        errorOnNoContent: false,
        cacheTime: CACHE_TIME
    }).then((res: JSONResponse<APIListData<TelSys>>): EntitySelectOption<number, TelSys>[] => {
        const availableServers: EntitySelectOption<number, TelSys>[] =
            res?.Data?.Items?.map(
                ({
                    Description,
                    TelSysID,
                    TelSysTypeID,
                    PartitionLicences
                }: TelSys): EntitySelectOption<number, TelSys> => ({
                    label: Description,
                    value: TelSysID,
                    original: { Description, TelSysID, PartitionLicences, TelSysTypeID }
                })
            ) ?? []

        return userInfo && userInfo.Scope.TelSysID.includes(ALL_VALUE)
            ? [ALL_TEL_OPTION, ...availableServers]
            : availableServers
    })

// Get the list of Tel Servers.
export const getTelServersAsSelectOptions = (
    cmeID: string,
    recursive?: boolean,
    errorOnNoContent: boolean = true,
    ...licenceLevel: string[]
): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(
        telServerURL(cmeID, recursive, [TelSysFields.PARTITION_LICENCES], ...licenceLevel),
        {
            cacheTime: CACHE_TIME,
            errorOnNoContent
        }
    ).then((res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
        const availableServers: SelectOptions<number> =
            res?.Data?.Items?.map(
                ({ Description, TelSysID, PartitionLicences }: TelSys): SelectOption<number> => ({
                    label: Description,
                    value: TelSysID,
                    licences: PartitionLicences
                })
            ) ?? []

        return availableServers
    })

export const getTelServersAsSelectOptionsWithFilters = (
    cmeID: string,
    filteredTelSysTypes: TelSysTypes[],
    recursive?: boolean,
    ...licenceLevel: string[]
): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(
        telServerURL(cmeID, recursive, [TelSysFields.PARTITION_LICENCES, TelSysFields.TELSYS_TYPE], ...licenceLevel),
        {
            cacheTime: CACHE_TIME,
            errorOnNoContent: true
        }
    ).then((res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
        const availableServers: SelectOptions<number> =
            res?.Data?.Items?.filter((telSys: TelSys): boolean =>
                isVarNotEmpty(telSys?.TelSysType) ? filteredTelSysTypes.includes(telSys?.TelSysType) : true
            )?.map(
                ({ Description, TelSysID, PartitionLicences }: TelSys): SelectOption<number> => ({
                    label: Description,
                    value: TelSysID,
                    licences: PartitionLicences
                })
            ) ?? []

        return availableServers
    })

// Get all tel servers for current user and return them as a list of SelectOptions.
export const getAllTelServersAsSelectOptions = (): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<TelSys>>(getAllTelSysAccessibleByUser(), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIListData<TelSys>>): SelectOptions<number> => {
            const availableServers: SelectOptions<number> =
                res?.Data?.Items?.map(
                    ({ Description, TelSysID }: TelSys): SelectOption<number> => ({
                        label: Description,
                        value: TelSysID
                    })
                ) ?? []

            return availableServers
        }
    )

// Get a list of partitions for a given tel server.
export const getPartitionsForTelServer = (telServerID: number): Promise<SelectOptions<number>> =>
    requestGetJSON<APIListData<Partition>>(partitionsURL(telServerID), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIListData<Partition>>): SelectOptions<number> => {
            const availablePartitions: SelectOptions<number> =
                res?.Data?.Items?.map(
                    ({ Description, PartIDInTelSys, LicensedForRptUserType }: Partition): SelectOption<number> => ({
                        label: Description,
                        value: PartIDInTelSys,
                        licencedForRptType: LicensedForRptUserType
                    })
                ) ?? []

            return availablePartitions
        }
    )

// Helper function for getting devices with a specified cache time
const fetchDevices = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    cacheTime?: number,
    ...deviceFields: string[]
): Promise<DeviceSelectItem[]> => {
    const devicesPromise: Promise<JSONResponse<APIData<GenericDeviceCollections>>> = device
        ? requestGetJSON<APIData<GenericDeviceCollections>>(
              groupMembershipURL(telServerID, partition, device, deviceTypeFilter),
              { cacheTime }
          )
        : requestGetJSON<APIData<GenericDeviceCollections>>(
              devicesURL(telServerID.toString(), partition.toString(), monitoredOnly, deviceTypeFilter, deviceFields),
              { cacheTime }
          )

    return devicesPromise.then((res: JSONResponse<APIData<GenericDeviceCollections>>): DeviceSelectItem[] => {
        const devices: Nullable<Device[]> = res?.Data?.Items ?? res?.Data?.Devices
        const availableDevices: DeviceSelectItem[] =
            devices?.map(
                (
                    {
                        Description,
                        DeviceNumber,
                        DeviceTypeID,
                        Users,
                        TelSysUsername,
                        IDOnTelSys,
                        TelSysUserID,
                        ACDAgentNumber,
                        OverrideMonitoring,
                        LicenseType,
                        QueueStatus,
                        DisableRTMonitoring,
                        QueueCompatibility,
                        IDOnTelSysOwner,
                        CallQueueCaptureErrorMessage
                    }: Device,
                    index: number
                ): DeviceSelectItem => ({
                    label: `${DeviceNumber} - ${Description}`,
                    value: DeviceNumber,
                    type: DeviceTypeID?.toString(),
                    original: {
                        Description,
                        DeviceNumber,
                        DeviceTypeID: DeviceTypeID ?? 0,
                        Users,
                        TelSysUsername,
                        IDOnTelSys,
                        TelSysUserID,
                        ACDAgentNumber,
                        OverrideMonitoring,
                        LicenceType: LicenseType,
                        QueueStatus,
                        DisableRTMonitoring,
                        QueueCompatibility,
                        IDOnTelSysOwner,
                        CallQueueCaptureErrorMessage
                    },
                    isAgent: !!ACDAgentNumber,
                    index
                })
            ) ?? []

        return availableDevices
    })
}

// Get a list of devices, with default cache time.
export const getDevicesFromPartition = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    ...deviceFields: string[]
): Promise<DeviceSelectItem[]> =>
    fetchDevices(telServerID, partition, monitoredOnly, deviceTypeFilter, device, CACHE_TIME, ...deviceFields)

// Get a list of devices, with a specified cache time.
export const getDevicesFromPartitionByCache = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    cacheTime: number = CACHE_TIME,
    ...deviceFields: string[]
): Promise<DeviceSelectItem[]> =>
    fetchDevices(telServerID, partition, monitoredOnly, deviceTypeFilter, device, cacheTime, ...deviceFields)

// Get a list of agents.
export const getAgentsFromPartition = (
    telServerID: number,
    partition: number,
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    device?: string,
    ...agentFields: string[]
): Promise<AgentSelectItem[]> => {
    /* eslint-disable */
    const agentsPromises: Promise<JSONResponse<APIData<GenericAgentsCollections>>> = device
        ? requestGetJSON<APIData<GenericAgentsCollections>>(
              groupMembershipURL(telServerID, partition, device, deviceTypeFilter),
              { cacheTime: CACHE_TIME }
          )
        : requestGetJSON<APIData<GenericAgentsCollections>>(
              agentsURL(`${telServerID}`, `${partition}`, monitoredOnly, agentFields),
              {
                  cacheTime: CACHE_TIME
              }
          )
    /* eslint-enable */

    return agentsPromises.then((res: JSONResponse<APIData<GenericAgentsCollections>>): SelectItem[] => {
        const agents: Nullable<Agent[]> = res?.Data?.Items ?? res?.Data?.ACDAgents
        const availableAgents: AgentSelectItem[] =
            agents?.map(
                ({
                    Description,
                    AgentNumber,
                    IDOnTelSys,
                    TelSysUsername,
                    OverrideMonitoring
                }: Agent): AgentSelectItem => ({
                    label: `${AgentNumber} - ${Description}`,
                    value: AgentNumber,
                    original: { Description, AgentNumber, IDOnTelSys, TelSysUsername, OverrideMonitoring }
                })
            ) ?? []

        return availableAgents
    })
}

// Get a list of call recording integrations.
export const getCallRecordingIntegrations = (
    cmeID: string,
    telServerID: number,
    partitionID: number
): Promise<SelectOptions<string>> =>
    // eslint-disable-next-line
    requestGetJSON<CallRecordingIntegration[]>(callRecordingIntegrationsURL(cmeID, telServerID, partitionID), {
        cacheTime: EXTENDED_CACHE_TIME
    }).then((res: JSONResponse<CallRecordingIntegration[]>): SelectOptions<string> => {
        if (!Array.isArray(res)) {
            return []
        }

        const availableCRIs: SelectOptions<string> =
            res?.map(
                (cri: CallRecordingIntegration): SelectOption<string> => ({
                    label: cri.Description,
                    value: cri.ID
                })
            ) ?? []

        return availableCRIs
    })

// Get a telephony server.
export const getTelephonyServer = (
    telServerID: number | string,
    ...telSysFields: string[]
): Promise<Nullable<IndividualTelItem>> =>
    requestGetJSON<APIData<IndividualTelItem>>(telSysURL(Number(telServerID), PARTITION_LICENCES, ...telSysFields), {
        cacheTime: CACHE_TIME
    }).then((res: JSONResponse<APIData<IndividualTelItem>>): Nullable<IndividualTelItem> => res?.Data)

// Get a partition.
export const getPartition = (telServerID: number, partitionID: number): Promise<Nullable<PartitionValues>> =>
    requestGetJSON<APIData<PartitionValues>>(partitionURL(telServerID, partitionID), { cacheTime: CACHE_TIME }).then(
        (res: JSONResponse<APIData<PartitionValues>>): Nullable<PartitionValues> => res?.Data
    )

// Get default reports for a given reporting level.
export const getDefaultReports = (
    reportingLevel: string,
    telSysType: Nullable<TelSysTypes>,
    telSysID?: number
): Promise<Nullable<SelectOptions<string>>> =>
    requestGetJSON<DefaultReportItem[]>(defaultReportsListURL(reportingLevel.replace(/\s/g, '_'), telSysID), {
        cacheTime: CACHE_TIME
    }).then((defaultReports: JSONResponse<DefaultReportItem[]>): Nullable<SelectOptions<string>> => {
        if (!defaultReports) {
            return null
        }

        const language: CLanguageHandler = CLanguageHandler.getInstance()
        language.loadBundle(ReportingDefaultReportSets.NAME)

        return defaultReports
            .sort((a: DefaultReportItem, b: DefaultReportItem) => a.Weight - b.Weight)
            .filter((report: DefaultReportItem): boolean => !IsVarEmpty(report))
            .map(({ ID, SelectedByDefault }: DefaultReportItem): SelectOption<string> => {
                const labelLanguage: string = language.getWithMSTCheck(`DefaultSet_${ID}`, telSysType)
                const label: string = IsStringEmpty(labelLanguage) ? ID.replace(/_/g, ' ') : labelLanguage

                return {
                    label,
                    value: ID,
                    defaultSelected: SelectedByDefault
                }
            })
    })

export const getSimulationModes = (): Promise<Nullable<SelectOptions<string>>> =>
    requestGetJSON<string[]>(getSimulationModesURL()).then(
        (simulationModes: JSONResponse<string[]>): Nullable<SelectOptions<string>> =>
            simulationModes?.map(
                (mode: string): SelectOption<string> => ({
                    label: mode,
                    value: mode
                })
            )
    )

export const getLocales = (): Promise<Nullable<LocaleEntries>> =>
    requestGetJSON<LocaleEntries>(LOCALES_URL).then(
        (locales: JSONResponse<LocaleEntries>): Nullable<LocaleEntries> => locales
    )

export const getClientConfigurationsList = (
    telSysID: string | number,
    partitionID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string
): Promise<Nullable<ClientConfigurationsResponse>> =>
    requestGetJSON(getClientConfigurationListURL(telSysID, partitionID, ANY_DEVICE_VALUE, offset, limit, sort, search))

export const getCRMConfigurationsList = (
    telSysID: string | number,
    partitionID: string | number,
    offset?: number,
    limit?: number,
    sort?: string,
    search?: string,
    crmType?: string
): Promise<Nullable<CRMConfigurationsResponse>> =>
    requestGetJSON(
        getCRMConfigurationListURL(telSysID, partitionID, ANY_DEVICE_VALUE, offset, limit, sort, search, crmType)
    )
